import { APP_ROUTE_PATTERNS } from "./appRoutePatterns.generated";

/**
 * Operational telemetry reports a request or page as its app route pattern,
 * never as the real path: a dynamic segment can be a capability (a call id is
 * a guest join link, an invite token admits its holder), a name or an email.
 *
 * `/call/acme-board-q3` becomes `/call/:callId`. A path that matches no route
 * fails closed: every segment that is not a static segment of some app route
 * becomes `:id`, so an unknown or newly added dynamic route never leaks.
 */

type TrieNode = {
  pattern?: string;
  statics: Map<string, TrieNode>;
  dynamic?: TrieNode;
  /** `:...x`: one or more remaining segments. */
  catchAll?: string;
};

const UNKNOWN_SEGMENT = ":id";
const MAX_ROUTE_LENGTH = 160;

function emptyNode(): TrieNode {
  return { statics: new Map() };
}

function buildIndex(patterns: readonly string[]): { root: TrieNode; staticSegments: ReadonlySet<string> } {
  const root = emptyNode();
  const staticSegments = new Set<string>();
  for (const pattern of patterns) {
    let node = root;
    let terminal = true;
    for (const segment of pattern.split("/").filter(Boolean)) {
      if (segment.startsWith(":...")) {
        node.catchAll = pattern;
        terminal = false;
        break;
      }
      if (segment.startsWith(":")) {
        node.dynamic ??= emptyNode();
        node = node.dynamic;
        continue;
      }
      staticSegments.add(segment);
      let child = node.statics.get(segment);
      if (!child) {
        child = emptyNode();
        node.statics.set(segment, child);
      }
      node = child;
    }
    if (terminal) node.pattern = pattern;
  }
  return { root, staticSegments };
}

/** Next's precedence: a static segment beats a dynamic one, which beats a catch-all. */
function match(node: TrieNode, segments: readonly string[], index: number): string | undefined {
  if (index === segments.length) return node.pattern;
  const staticChild = node.statics.get(segments[index]);
  const viaStatic = staticChild ? match(staticChild, segments, index + 1) : undefined;
  if (viaStatic) return viaStatic;
  const viaDynamic = node.dynamic ? match(node.dynamic, segments, index + 1) : undefined;
  return viaDynamic ?? node.catchAll;
}

/** The pattern matcher over an explicit route list (exported for tests). */
export function createRoutePatternMatcher(patterns: readonly string[]): (path: string) => string {
  const { root, staticSegments } = buildIndex(patterns);
  return (path) => {
    const segments = path.split(/[?#]/, 1)[0].split("/").filter(Boolean);
    const matched = match(root, segments, 0);
    if (matched) return matched;
    const masked = segments.map((segment) => (staticSegments.has(segment) ? segment : UNKNOWN_SEGMENT));
    return `/${masked.join("/")}`.slice(0, MAX_ROUTE_LENGTH);
  };
}

/** The app route pattern for a path (a query or fragment is ignored); see the module comment. */
export const toRoutePattern: (path: string) => string = createRoutePatternMatcher(APP_ROUTE_PATTERNS);
